import {
  ThemeProvider as MuiThemeProvider,
  Theme,
  ThemeOptions,
  createTheme as createThemeWithoutVars,
  useTheme,
} from '@mui/material';
import { deepmerge } from '@mui/utils';
import * as React from 'react';
import UnifyStyleContextProvider, {
  isLegacyThemeProviderEnabled,
  useThemeProviderInstanceId,
} from 'src/styles/context/unifyStyleContext';
import { colorSchemeAttribute } from 'src/styles/themes/constants';

export interface ThemeProviderProps<T extends Theme> {
  children?: React.ReactNode;
  /**
   * For Internal or LaunchDarkly `test-disable-wktheme` Use Only.
   * When true, the children will not use the legacy wkTheme.
   *
   * @default false
   */
  disableWkTheme?: boolean;

  /**
   * A legacy theme object. You can provide a function to extend the outer theme.
   *
   * Your theme must include `isLegacyWsdTheme: true`, or unexpected behavior will occur.
   *
   * To use a non-legacy theme, use `UnifyThemeProvider`.
   */
  theme:
    | (Partial<T> & { isLegacyWsdTheme: true })
    | ((outerTheme: T) => T & { isLegacyWsdTheme: true });
}

type $InternalThemeProviderProps$ = {
  /**
   * @internal for testing only
   */
  zz$$internal$$themeProviderInstanceId?: string;
};

/**
 * This component makes the `theme` available down the React tree.
 * It should preferably be used at **the root of your component tree**.
 *
 * @deprecated use UnifyThemeProvider instead.
 */
export function ThemeProvider(
  props: ThemeProviderProps<Theme> & $InternalThemeProviderProps$
): React.ReactElement {
  const {
    children: childrenProp,
    disableWkTheme = false,
    theme,
    ...rest
  } = props;
  const currentThemeContext = useTheme();

  if (
    process.env.NODE_ENV !== 'production' &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    theme.isLegacyWsdTheme === false
  ) {
    throw new TypeError(
      'UNIFY: The theme passed to `ThemeProvider` must be a "legacy WSD theme" (theme.isLegacyWsdTheme must be true). To use a non-legacy theme, try the `UnifyThemeProvider` component instead.'
    );
  }

  // FIXME (adl): The use of `currentThemeContext` here as a means to detect whether the provider
  // is nested inside one of our theme providers doesn't work for MFE contributions.
  // See: jsifyUiElement / ReactUiElement in the miccrofrontend repo for more info.
  // The MUI Chips being used here render using the default MUI theme: https://staging.wdesk.org/fews/v1/serve/cdn-dev:wdesk@7126129/a/QWNjb3VudB81NjMwODI0NTk/request-list?ld.test-disable-wktheme=true
  //
  // For now, I've wrapped the root of `_ThemedUiElementRoot` within the microfrontend repo
  // in its own UnifyThemeProvider in the "WrapElsInUnifyThemeProvider" branch to work around this.
  // But we should sync up with FEA to figure out if that is the right approach long-term.
  const legacyThemeProviderEnabled = isLegacyThemeProviderEnabled(
    currentThemeContext,
    disableWkTheme
  );
  const children = !legacyThemeProviderEnabled ? (
    childrenProp
  ) : (
    <LegacyWsdThemeProvider theme={theme} {...rest}>
      {childrenProp}
    </LegacyWsdThemeProvider>
  );

  return (
    <UnifyStyleContextProvider
      value={{
        colorSchemeAttribute,
        legacyWsdStyleSupport: true,
      }}
    >
      {children}
    </UnifyStyleContextProvider>
  );
}

// eslint-disable-next-line deprecation/deprecation
export default ThemeProvider;

function LegacyWsdThemeProvider(
  inProps: Omit<ThemeProviderProps<Theme>, 'disableWkTheme'> &
    $InternalThemeProviderProps$
): React.ReactElement {
  const instanceId = useThemeProviderInstanceId(inProps);
  const {
    children,
    theme: themeIn,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    zz$$internal$$themeProviderInstanceId,
    ...props
  } = inProps;
  const outerTheme = useTheme();
  // If our custom isLegacyWsdTheme key is not present, then this theme
  // is not nested within one of our themes.
  const themeIsNested = 'isLegacyWsdTheme' in outerTheme;
  const theme = {
    ...(typeof themeIn === 'function' ? themeIn(outerTheme) : themeIn),
    zz$$internal$$themeProviderInstanceId: instanceId,
  };

  function mergeThemes(outerTheme: Theme): Theme {
    const outerUsesCssVarsProvider = typeof outerTheme.vars != 'undefined';

    let mergedTheme: Theme = deepmerge(outerTheme, theme);

    if (outerUsesCssVarsProvider) {
      // A legacy WSD theme is being nested within a CssVarsProvider based theme.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error we have to delete the vars key so that MUI components don't try to look for value in that object.
      delete mergedTheme.vars;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      mergedTheme = createThemeWithoutVars(mergedTheme as ThemeOptions);
    }

    return mergedTheme;
  }

  return (
    <MuiThemeProvider theme={themeIsNested ? mergeThemes : theme} {...props}>
      {children}
    </MuiThemeProvider>
  );
}
